/* Product media showcase: images and videos from a product file-list metafield. */
(() => {
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Smallest transcode that still covers the space it renders into.
  function pickSource(sources, targetHeight) {
    if (!Array.isArray(sources) || sources.length === 0) return null;
    const sorted = [...sources].sort((a, b) => (a.height || 0) - (b.height || 0));
    const match = sorted.find((source) => (source.height || 0) >= targetHeight);
    return (match || sorted[sorted.length - 1]).url;
  }

  function deviceHeight(cssHeight, cap) {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    return Math.min(Math.round(cssHeight * dpr), cap);
  }

  let lightbox = null;

  class MediaLightbox {
    constructor() {
      this.items = [];
      this.index = 0;
      this.openedBy = null;

      this.el = document.createElement('div');
      this.el.className = 'pdp-media-lightbox';
      this.el.setAttribute('role', 'dialog');
      this.el.setAttribute('aria-modal', 'true');
      this.el.setAttribute('aria-label', 'Media viewer');
      this.el.innerHTML = `
        <div class="pdp-media-lightbox__stage"></div>
        <button type="button" class="pdp-media-lightbox__btn pdp-media-lightbox__close" aria-label="Close">
          <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>
        </button>
        <button type="button" class="pdp-media-lightbox__btn pdp-media-lightbox__prev" aria-label="Previous">
          <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m15 18-6-6 6-6"/></svg>
        </button>
        <button type="button" class="pdp-media-lightbox__btn pdp-media-lightbox__next" aria-label="Next">
          <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6"/></svg>
        </button>`;

      this.stage = this.el.querySelector('.pdp-media-lightbox__stage');
      this.prevBtn = this.el.querySelector('.pdp-media-lightbox__prev');
      this.nextBtn = this.el.querySelector('.pdp-media-lightbox__next');

      this.el.querySelector('.pdp-media-lightbox__close').addEventListener('click', () => this.close());
      this.prevBtn.addEventListener('click', () => this.show(this.index - 1));
      this.nextBtn.addEventListener('click', () => this.show(this.index + 1));
      this.el.addEventListener('click', (evt) => {
        if (evt.target === this.el) this.close();
      });

      this.keydownHandler = this.handleKeydown.bind(this);
      document.body.appendChild(this.el);
    }

    handleKeydown(evt) {
      if (evt.key === 'Escape') this.close();
      if (evt.key === 'ArrowLeft') this.show(this.index - 1);
      if (evt.key === 'ArrowRight') this.show(this.index + 1);
    }

    open(items, index, opener, aspect) {
      this.items = items;
      this.openedBy = opener;
      this.el.style.setProperty('--pdp-media-aspect', aspect || '9 / 16');

      if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();

      const hasSiblings = items.length > 1;
      this.prevBtn.hidden = !hasSiblings;
      this.nextBtn.hidden = !hasSiblings;

      this.scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      this.el.setAttribute('open', '');
      this.show(index);

      document.addEventListener('keydown', this.keydownHandler);
      if (typeof window.trapFocus === 'function') window.trapFocus(this.el);
    }

    show(index) {
      if (index < 0) index = this.items.length - 1;
      if (index > this.items.length - 1) index = 0;
      this.index = index;

      const item = this.items[index];
      this.stage.innerHTML = '';
      this.stage.style.removeProperty('aspect-ratio');

      const source = item.type === 'video'
        ? pickSource(item.sources, deviceHeight(this.stage.clientHeight || 720, 1920))
        : null;

      if (item.type === 'video' && source) {
        const video = document.createElement('video');
        video.setAttribute('playsinline', '');
        video.controls = true;
        video.loop = true;
        video.preload = 'auto';
        if (item.poster) video.poster = item.poster;
        video.addEventListener('loadedmetadata', () => {
          if (video.videoWidth && video.videoHeight) {
            this.fitStage(video.videoWidth, video.videoHeight);
          }
        }, { once: true });
        video.src = source;
        this.stage.appendChild(video);
        video.play().catch(() => {
          // Audible autoplay blocked.
          video.muted = true;
          video.play().catch(() => {});
        });
      } else {
        // Images, and videos with no playable transcode (poster only).
        const img = document.createElement('img');
        img.addEventListener('load', () => this.fitStage(img.naturalWidth, img.naturalHeight), { once: true });
        img.src = item.src || item.poster;
        if (item.srcset) img.srcset = item.srcset;
        img.sizes = '92vw';
        img.alt = item.alt || '';
        this.stage.appendChild(img);
      }

      if (item.credit) {
        const credit = document.createElement('p');
        credit.className = 'pdp-media-lightbox__credit';
        credit.textContent = item.credit;
        this.stage.appendChild(credit);
      }
    }

    // Take the media's own ratio so mixed-ratio content isn't letterboxed.
    fitStage(width, height) {
      if (!width || !height) return;
      this.stage.style.aspectRatio = `${width} / ${height}`;
    }

    close() {
      this.el.removeAttribute('open');
      this.stage.innerHTML = '';
      document.body.style.overflow = '';
      document.removeEventListener('keydown', this.keydownHandler);

      if (typeof window.removeTrapFocus === 'function') {
        window.removeTrapFocus(this.openedBy);
      } else if (this.openedBy) {
        this.openedBy.focus();
      }

      document.dispatchEvent(new CustomEvent('pdp-media-lightbox:close'));
    }
  }

  class ProductMediaShowcase extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.tiles = [...this.querySelectorAll('.pdp-media__item')];
      this.autoplay = this.dataset.autoplay === 'true' && !REDUCED_MOTION.matches;
      this.lightboxEnabled = this.dataset.lightbox === 'true';

      this.slider = this.querySelector('.pdp-media__slider');
      this.grid = this.querySelector('.pdp-media__grid');

      this.syncOverflow();
      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(() => this.syncOverflow());
        this.resizeObserver.observe(this.slider);
      }

      if (this.autoplay) this.observeVideos();
      if (this.lightboxEnabled) this.bindLightbox();
    }

    // The Liquid guess is item count only; re-decide from the measured row.
    syncOverflow() {
      if (!this.slider || !this.grid) return;
      const gap = parseFloat(getComputedStyle(this.grid).columnGap) || 0;
      // Tiles, not items: the last item carries the trailing gutter as a border.
      const width = this.tiles.reduce((total, item) => {
        const tile = item.querySelector('.pdp-media__tile');
        return total + (tile ? tile.offsetWidth : item.offsetWidth);
      }, 0) + gap * Math.max(this.tiles.length - 1, 0);

      const sliderStyle = getComputedStyle(this.slider);
      const available = this.slider.clientWidth
        - parseFloat(sliderStyle.paddingLeft || 0)
        - parseFloat(sliderStyle.paddingRight || 0);

      this.classList.toggle('pdp-media--static', width - available <= 2);
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.observer) this.observer.disconnect();
      if (this.resumeHandler) document.removeEventListener('pdp-media-lightbox:close', this.resumeHandler);
    }

    observeVideos() {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target;
            if (entry.isIntersecting) {
              this.playTile(video);
            } else {
              video.pause();
              video.closest('.pdp-media__item').classList.remove('is-playing');
            }
          });
        },
        { rootMargin: '200px 0px' }
      );

      this.querySelectorAll('.pdp-media__video').forEach((video) => {
        video.addEventListener('playing', () => {
          video.classList.add('is-playing');
          video.closest('.pdp-media__item').classList.add('is-playing');
        });
        this.observer.observe(video);
      });

      // pauseAllMedia() stops these loops; restart the visible ones on close.
      this.resumeHandler = () => {
        this.querySelectorAll('.pdp-media__video').forEach((video) => {
          const rect = video.getBoundingClientRect();
          if (rect.bottom > 0 && rect.top < window.innerHeight) this.playTile(video);
        });
      };
      document.addEventListener('pdp-media-lightbox:close', this.resumeHandler);
    }

    playTile(video) {
      if (!video.src) {
        let sources = [];
        try {
          sources = JSON.parse(video.dataset.sources || '[]');
        } catch {
          return;
        }
        const url = pickSource(sources, deviceHeight(video.clientHeight || 480, 1280));
        if (!url) return;
        video.src = url;
      }

      video.muted = true;
      video.play().catch(() => {});
    }

    bindLightbox() {
      this.items = this.tiles.map((tile) => {
        const creditEl = tile.querySelector('.pdp-media__credit');
        const credit = creditEl ? creditEl.textContent.trim() : '';

        const video = tile.querySelector('.pdp-media__video');
        if (video) {
          let sources = [];
          try {
            sources = JSON.parse(video.dataset.sources || '[]');
          } catch {
            sources = [];
          }
          return { type: 'video', sources, poster: video.dataset.poster, credit };
        }

        const img = tile.querySelector('.pdp-media__img');
        return {
          type: 'image',
          src: img ? img.currentSrc || img.src : '',
          srcset: img ? img.getAttribute('srcset') : '',
          alt: img ? img.alt : '',
          credit
        };
      });

      this.addEventListener('click', (evt) => {
        const button = evt.target.closest('[data-pdp-media-index]');
        if (!button || !this.contains(button)) return;

        // Ignore the click that ends a drag.
        if (button.closest('.slider.is-dragging')) return;

        this.querySelectorAll('.pdp-media__video').forEach((video) => {
          video.pause();
          video.closest('.pdp-media__item').classList.remove('is-playing');
        });

        if (!lightbox) lightbox = new MediaLightbox();
        // From the DOM: skipped files leave gaps in the rendered numbering.
        const index = this.tiles.indexOf(button.closest('.pdp-media__item'));

        lightbox.open(
          this.items,
          index < 0 ? 0 : index,
          button,
          getComputedStyle(this).getPropertyValue('--pdp-media-aspect')
        );
      });
    }
  }

  customElements.define('pdp-media-showcase', ProductMediaShowcase);
})();
